import { useEffect, useState } from 'react';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Checkbox, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';

export interface BlockCustomerModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (opts: { reason?: string; also_on_whatsapp?: boolean }) => void;
  /** A WhatsApp conversation, where the number can also be blocked on WhatsApp itself. */
  whatsapp: boolean;
  pending: boolean;
}

/**
 * Block the customer in a conversation (15 September 2026): a reason for the
 * team, and on WhatsApp the choice to block the number there too. Says plainly
 * what blocking does before anyone confirms it.
 */
export function BlockCustomerModal({ opened, onClose, onConfirm, whatsapp, pending }: BlockCustomerModalProps) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [alsoOnWhatsapp, setAlsoOnWhatsapp] = useState(false);

  useEffect(() => {
    if (opened) { setReason(''); setAlsoOnWhatsapp(false); }
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('inbox.block.title')} size="md">
      <Stack gap="md">
        <Text role="bodySmall">{t('inbox.block.body')}</Text>
        <Textarea
          label={t('inbox.block.reason')}
          description={t('inbox.block.reasonHint')}
          value={reason}
          minRows={2}
          maxLength={500}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {whatsapp && (
          <Checkbox
            label={t('inbox.block.alsoWhatsapp')}
            description={t('inbox.block.alsoWhatsappHint')}
            checked={alsoOnWhatsapp}
            onChange={setAlsoOnWhatsapp}
          />
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button
            loading={pending}
            onClick={() => onConfirm({ reason: reason.trim() || undefined, also_on_whatsapp: whatsapp && alsoOnWhatsapp })}
          >
            {t('inbox.block.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
